import { type DisplayDocument, Document } from '@startuptools/business-ui/document/Document';
import { apiClient } from '../../../../common/http';
import {
  useActiveCompanyStripeSubLazyQuery,
  useMeetingPaidForConcernsLazyQuery,
  useOrErrorsLazyQuery,
  useWppErrorsLazyQuery,
} from '../../../../graphql/react-operations';
import { DocGenRelationName } from '@startuptools/common/doc-gen';
import { useApolloClient } from '@apollo/client';
import {
  type GqlCompany,
  GqlDocSystemTagKind,
  GqlDocumentSignatureMethod,
  type GqlIdentity,
  type GqlMeetingConcern,
  type GqlOptionsRegistration,
  type GqlValidatorStatus,
  type GqlWarrantParticipant,
  type GqlWarrantProgram,
  GqlWarrantProgramKind,
} from '../../../../graphql/base-types.graphql';
import { checkNeedsPayment, hasValidatorErrors } from '../../utils';
import { Trans } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { Divider } from '@startuptools/ui/Divider';
import { Stack } from '@startuptools/ui/Stack';
import { useState } from 'react';
import { DocumentSignatures } from '../../../../components/document-signatures/DocumentSignatures';
import { TagsInput } from '../../../../components/document-dialog/DocumentDialog';
import { omit } from 'lodash-es';
import { type DisplayIdentity } from '@startuptools/business-ui/identity/Identity';

import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  useDocGenNiceAgreementMutation,
  useDocGenQesoAgreementMutation,
  useDocGenWarrantAgreementMutation,
  useDocGenWiseAgreementMutation,
} from './WarrantAgreementDocument.graphql';
import { StepOrSubscribersDocument } from '../../../stock-options/common/components/steps/or-subscribers/or-subscribers.graphql';
import { OptionsDocumentsDocument } from '../../../stock-options/common/components/main-steps/options-documents/OptionsDocuments.graphql';
import { InnerStripe } from '../../../../components/stripe/Stripe';
import { useNavigate } from '../../../../components/Navigate';

const getDefaultFilename = (
  warrantKind: GqlWarrantProgramKind,
  companyName: string,
  participantName: string,
  programName: string,
  orDescription: string | null | undefined,
) => {
  const prefix: Record<GqlWarrantProgramKind, string> = {
    [GqlWarrantProgramKind.Wise]: 'WISE-avtal',
    [GqlWarrantProgramKind.Nice]: 'NICE-avtal',
    [GqlWarrantProgramKind.Employee]: 'Optionsavtal',
    [GqlWarrantProgramKind.Warrant]: 'Optionsavtal',
  };
  let filename = `${prefix[warrantKind]} - ${participantName} - ${companyName} - ${programName}`;
  if (warrantKind === GqlWarrantProgramKind.Warrant) {
    filename += ` - ${orDescription}`;
  }
  return `${filename}.pdf`;
};

const docKindMap: Record<GqlWarrantProgramKind, GqlDocSystemTagKind> = {
  [GqlWarrantProgramKind.Warrant]: GqlDocSystemTagKind.WarrantAgreement,
  [GqlWarrantProgramKind.Employee]: GqlDocSystemTagKind.EmploymentAgreement,
  [GqlWarrantProgramKind.Wise]: GqlDocSystemTagKind.WiseAgreement,
  [GqlWarrantProgramKind.Nice]: GqlDocSystemTagKind.NiceAgreement,
};

const REFETCH_QUERIES = [StepOrSubscribersDocument, OptionsDocumentsDocument];

const useDocGen = (kind: GqlWarrantProgramKind) => {
  const warrant = useDocGenWarrantAgreementMutation({ refetchQueries: REFETCH_QUERIES, awaitRefetchQueries: true });
  const employee = useDocGenQesoAgreementMutation({ refetchQueries: REFETCH_QUERIES, awaitRefetchQueries: true });
  const wise = useDocGenWiseAgreementMutation({ refetchQueries: REFETCH_QUERIES, awaitRefetchQueries: true });
  const nice = useDocGenNiceAgreementMutation({ refetchQueries: REFETCH_QUERIES, awaitRefetchQueries: true });
  return {
    [GqlWarrantProgramKind.Warrant]: warrant,
    [GqlWarrantProgramKind.Employee]: employee,
    [GqlWarrantProgramKind.Wise]: wise,
    [GqlWarrantProgramKind.Nice]: nice,
  }[kind];
};

export const WarrantAgreementDocument = ({
  company,
  warrantProgram,
  optionsRegistration,
  participant,
  navigateToDecisionStep,
}: {
  company: Pick<GqlCompany, 'id' | 'name' | 'allowShortQeso'>;
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'name' | 'warrantKind' | 'usesStepper' | 'paidForAt' | 'companyId'> & {
    meetingConcern?: Pick<GqlMeetingConcern, 'id' | 'meetingId'> | null;
  };
  optionsRegistration: Pick<GqlOptionsRegistration, 'id' | 'description'>;
  participant: Pick<GqlWarrantParticipant, 'id' | 'optionsRegistrationId'> & {
    identity: DisplayIdentity & Pick<GqlIdentity, 'juridicalKind'>;
    latestAgreementDoc?: DisplayDocument | undefined | null;
  };
  navigateToDecisionStep: () => void;
}) => {
  const companyId = useCompanyId();
  const gqlClient = useApolloClient();
  const [showStripeDialog, setShowStripeDialog] = useState<{
    open: boolean;
    signatureMethod?: GqlDocumentSignatureMethod;
  }>({ open: false });

  const [genDoc] = useDocGen(warrantProgram.warrantKind);
  const [fetchPaidFor] = useMeetingPaidForConcernsLazyQuery({ fetchPolicy: 'no-cache' });
  const { navigate } = useNavigate();

  const [doGetActiveSub] = useActiveCompanyStripeSubLazyQuery({ variables: { companyId }, fetchPolicy: 'no-cache' });

  const [fetchWppErrors] = useWppErrorsLazyQuery({
    variables: { companyId: company.id, id: participant.id },
    fetchPolicy: 'no-cache',
  });

  const [fetchOrErrors] = useOrErrorsLazyQuery({
    variables: { companyId: company.id, id: participant.optionsRegistrationId },
    fetchPolicy: 'no-cache',
  });

  const defaultFileName = getDefaultFilename(
    warrantProgram.warrantKind,
    company.name,
    participant.identity.name,
    warrantProgram.name ?? '',
    optionsRegistration.description,
  );

  return (
    <>
      <Document
        allowDigitalSignature={warrantProgram.usesStepper}
        upload={async ({ file, signDigitally }) => {
          await apiClient.uploadFile(
            file,
            defaultFileName,
            company.id,
            {
              relation_id: participant.id,
              relation_name: DocGenRelationName.WarrantParticipantId,
            },
            signDigitally,
            docKindMap[warrantProgram.warrantKind],
          );
          await gqlClient.refetchQueries({ include: REFETCH_QUERIES });
        }}
        tagsSlot={
          participant?.latestAgreementDoc && (
            <Stack direction="row" gap={2}>
              <TagsInput document={participant.latestAgreementDoc} />
            </Stack>
          )
        }
        signaturesSlot={
          participant?.latestAgreementDoc?.signatureMethod === GqlDocumentSignatureMethod.Digital && (
            <>
              <Divider />
              <DocumentSignatures
                documentId={participant.latestAgreementDoc.id}
                scriveStatus={participant.latestAgreementDoc.scriveStatus}
                zignedAgreementStatus={participant.latestAgreementDoc.zignedAgreementStatus}
                signAllowedAction={participant.latestAgreementDoc.signAllowedAction}
              />
            </>
          )
        }
        document={participant?.latestAgreementDoc}
        defaultFileName={defaultFileName}
        generate={
          warrantProgram.usesStepper
            ? async signatureMethod => {
                if (warrantProgram.paidForAt) {
                  return genDoc({
                    variables: {
                      companyId: warrantProgram.companyId,
                      warrantParticipantId: participant.id,
                      signatureMethod,
                    },
                  });
                }
                const id = warrantProgram?.meetingConcern?.meetingId;
                if (!id) {
                  throw new Error('Unexpected state: no meetingId');
                }
                const { data } = await fetchPaidFor({ variables: { companyId: company.id, id } });
                const paidFor = data?.meetingPaidForConcerns;
                if (!paidFor) {
                  throw new Error('Unexpected state: no paidFor');
                }
                const { data: activeSubData } = await doGetActiveSub();
                if (!activeSubData) {
                  throw new Error('Could not get active subscription');
                }
                const allMcs = [...paidFor.shareIssues, ...paidFor.warrantPrograms];
                const needsPayment = checkNeedsPayment(activeSubData.activeCompanyStripeSubscription, allMcs);
                if (needsPayment) {
                  setShowStripeDialog({ open: true, signatureMethod });
                  return;
                }
                return genDoc({
                  variables: { companyId: company.id, warrantParticipantId: participant.id, signatureMethod },
                });
              }
            : undefined
        }
        preview={
          warrantProgram.usesStepper
            ? () => {
                return apiClient.previewWarrantAgreement(
                  company.id,
                  warrantProgram.id,
                  participant.id,
                  warrantProgram.warrantKind,
                );
              }
            : undefined
        }
        validate={
          warrantProgram.usesStepper
            ? async () => {
                const { data: orErrorData } = await fetchOrErrors();
                let wppErrors = {};
                if (
                  warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant ||
                  warrantProgram.warrantKind === GqlWarrantProgramKind.Employee
                ) {
                  const { data } = await fetchWppErrors({ fetchPolicy: 'no-cache' });
                  wppErrors = omit(data?.warrantParticipantErrors, 'paymentStatus');
                }
                const errorsToIgnore = ['participants', 'todos'];
                if (warrantProgram.warrantKind === GqlWarrantProgramKind.Warrant) {
                  errorsToIgnore.push('signsRegForm');
                }
                const orErrors = omit(orErrorData?.optionsRegistrationErrors, ...errorsToIgnore) as Record<
                  string,
                  GqlValidatorStatus & { action?: React.ReactNode }
                >;

                if (hasValidatorErrors(orErrors) || hasValidatorErrors(wppErrors)) {
                  /* could do this more declaratively, but one step at at time...*/
                  // @ts-expect-error TS18048
                  orErrors.decision.action = (
                    <Button color="warning" variant="soft" onClick={navigateToDecisionStep}>
                      <Trans id="Go to decision" />
                    </Button>
                  );

                  return {
                    isValid: false,
                    errors: [
                      ...(Object.values(orErrors) as GqlValidatorStatus[]),
                      ...(Object.values(wppErrors) as GqlValidatorStatus[]),
                    ],
                  };
                }
                return { isValid: true, errors: undefined };
              }
            : () => Promise.resolve({ isValid: true, errors: undefined })
        }
      />
      {showStripeDialog.open && warrantProgram.meetingConcern?.meetingId && (
        <InnerStripe
          companyId={company.id}
          meetingId={warrantProgram.meetingConcern?.meetingId}
          onCancel={() => setShowStripeDialog({ open: false })}
          onPaid={() => {
            setShowStripeDialog({ open: false });
            return genDoc({
              variables: {
                companyId: company.id,
                warrantParticipantId: participant.id,
                signatureMethod: showStripeDialog.signatureMethod ?? GqlDocumentSignatureMethod.Analogue,
              },
            });
          }}
          onBillingRedirect={() => navigate('/settings/billing')}
        />
      )}
    </>
  );
};
